import { useEffect, useState } from 'react';
import { useMasters } from './MastersProvider';
import { fetchRecurringSettings, saveFiscalSettings, savePayerLabels, saveRecurringSettings } from './masterRepo';
import { useToast } from '@core/components/Toast';
import { fiscalYearOf, todayStr } from '@core/lib/date';
import TermsEditor from '@core/partners/TermsEditor';
import { fetchPaymentTermsSettings, savePaymentTermsSettings } from '@core/partners/termsSettings';
import type { PaymentTerms } from '@core/lib/types';

export default function SettingsTab() {
  const { fiscal, payerLabels, reload } = useMasters();
  const toast = useToast();
  const [startMonth, setStartMonth] = useState(fiscal.start_month);
  const [labels, setLabels] = useState(payerLabels);
  const [busy, setBusy] = useState(false);
  /** 繰り返し支払い(2026-09-07 指示): 自動処理のON/OFFと、支払日の何日前に経理へ知らせるか */
  const [autoEnabled, setAutoEnabled] = useState(false);
  const [noticeDays, setNoticeDays] = useState(1);
  /** 支払いページ(③-3)に出す日数(2026-09-08 指示: 支払日の1週間前から) */
  const [displayDays, setDisplayDays] = useState(7);
  /** ドライバー報酬の共通の支払条件(2026-10-23 段階2) */
  const [driverPay, setDriverPay] = useState<PaymentTerms | null>(null);
  // 読み込めなかったときは保存させない(空で上書きしないため)
  const [driverPayLoaded, setDriverPayLoaded] = useState(false);
  useEffect(() => {
    fetchPaymentTermsSettings()
      .then((s) => {
        setDriverPay(s.driver_pay);
        setDriverPayLoaded(true);
      })
      .catch(() => toast('ドライバー報酬の支払条件を読み込めませんでした(保存はできません)'));
  }, [toast]);

  async function saveDriverPay() {
    setBusy(true);
    try {
      await savePaymentTermsSettings({ driver_pay: driverPay });
      toast('ドライバー報酬の支払条件を保存しました');
    } catch (e) {
      toast(e instanceof Error ? e.message : '保存に失敗しました');
    } finally {
      setBusy(false);
    }
  }

  useEffect(() => {
    fetchRecurringSettings()
      .then((s) => {
        setAutoEnabled(s.auto_enabled);
        setNoticeDays(s.notice_days_before);
        setDisplayDays(s.display_days_before);
      })
      .catch(() => undefined);
  }, []);

  async function saveRecurring() {
    setBusy(true);
    try {
      await saveRecurringSettings({ auto_enabled: autoEnabled, notice_days_before: noticeDays, display_days_before: displayDays });
      toast('繰り返し支払いの設定を保存しました');
    } catch (e) {
      toast(e instanceof Error ? e.message : '保存に失敗しました');
    } finally {
      setBusy(false);
    }
  }

  async function saveLabels() {
    if (!labels.self.trim() || !labels.bucho.trim() || !labels.accounting.trim()) {
      toast('表示名は空にできません');
      return;
    }
    setBusy(true);
    try {
      await savePayerLabels({
        self: labels.self.trim(),
        bucho: labels.bucho.trim(),
        accounting: labels.accounting.trim(),
      });
      await reload();
      toast('支払い者の表示名を保存しました');
    } catch (e) {
      toast(e instanceof Error ? e.message : '保存に失敗しました');
    } finally {
      setBusy(false);
    }
  }

  async function save() {
    setBusy(true);
    try {
      await saveFiscalSettings({ start_month: startMonth });
      await reload();
      toast('年度の設定を保存しました');
    } catch (e) {
      toast(e instanceof Error ? e.message : '保存に失敗しました');
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <div className="card">
        <h3>ドライバー報酬の支払条件(全員共通)</h3>
        <p className="hint" style={{ margin: '0 0 10px' }}>
          外部システム(Routine)から届くドライバー報酬の支払期日の決め方です。下請け・取引先の条件は「取引先」タブで1社ずつ設定します。
        </p>
        <TermsEditor label="支払条件" value={driverPay} onChange={setDriverPay} />
        <button className="btn-green" onClick={() => void saveDriverPay()} disabled={busy || !driverPayLoaded}>
          保存
        </button>
      </div>

      <div className="card">
        <h3>年度(決算期)</h3>
        <div className="form-grid">
          <div>
            <label>年度の開始月</label>
            <select value={startMonth} onChange={(e) => setStartMonth(Number(e.target.value))}>
              {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => (
                <option key={m} value={m}>
                  {m}月
                </option>
              ))}
            </select>
          </div>
          <div>
            <label>今日({todayStr()})が属する年度</label>
            <input readOnly value={`${fiscalYearOf(todayStr(), startMonth)}年度`} />
          </div>
        </div>
        <button className="btn-green" onClick={() => void save()} disabled={busy}>
          保存
        </button>
        <p className="hint" style={{ marginTop: 10 }}>
          決算期は未確定のため暫定で4月開始にしています。確定したらここで変更してください
          (既存データの年度は保存済みの値のまま残ります)。
        </p>
      </div>

      <div className="card">
        <h3>繰り返し支払い</h3>
        <div className="form-grid">
          <div>
            <label>自動処理(支払い待ちの作成とお知らせ)</label>
            <select value={autoEnabled ? '1' : '0'} onChange={(e) => setAutoEnabled(e.target.value === '1')}>
              <option value="1">ON(経理・承認者がアプリを開いたときに実行)</option>
              <option value="0">OFF(手動で作る)</option>
            </select>
          </div>
          <div>
            <label>支払日の何日前に経理へ知らせるか</label>
            <select value={noticeDays} onChange={(e) => setNoticeDays(Number(e.target.value))}>
              {[0, 1, 2, 3, 5, 7, 10, 14].map((d) => (
                <option key={d} value={d}>
                  {d === 0 ? '当日' : `${d}日前`}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label>支払いページに出す期間(支払日の何日前から)</label>
            <select value={displayDays} onChange={(e) => setDisplayDays(Number(e.target.value))}>
              {[1, 2, 3, 5, 7, 10, 14, 21, 30].map((d) => (
                <option key={d} value={d}>
                  {`${d}日前から`}
                </option>
              ))}
            </select>
          </div>
        </div>
        <button className="btn-green" onClick={() => void saveRecurring()} disabled={busy}>
          保存
        </button>
        <p className="hint" style={{ marginTop: 10 }}>
          家賃・リースなどの繰り返し支払いは、当月〜翌々月の分が自動で「支払い待ち」になります。経理の「支払い待ち」画面には
          支払日の「出す期間」前から表示され、「知らせる日数」前に経理へお知らせが届きます(サーバー側の自動実行が使えないため、
          経理・承認者がアプリを開いたタイミングで判定します)。
        </p>
      </div>

      <div className="card">
        <h3>支払い者の表示名</h3>
        <div className="form-grid">
          <div>
            <label>自分で支払う(self)</label>
            <input value={labels.self} onChange={(e) => setLabels({ ...labels, self: e.target.value })} />
          </div>
          <div>
            <label>部門長に依頼する(bucho)</label>
            <input value={labels.bucho} onChange={(e) => setLabels({ ...labels, bucho: e.target.value })} />
          </div>
          <div>
            <label>経理に依頼する(accounting)</label>
            <input value={labels.accounting} onChange={(e) => setLabels({ ...labels, accounting: e.target.value })} />
          </div>
        </div>
        <button className="btn-green" onClick={() => void saveLabels()} disabled={busy}>
          表示名を保存
        </button>
        <p className="hint" style={{ marginTop: 10 }}>
          支払い者は承認フローの動作(誰に通知し誰が支払うか)と直結しているため、
          選択肢の追加・削除はできません。表示名のみここで変更できます。
        </p>
      </div>

      <div className="card">
        <h3>消費税</h3>
        <p>現在の設定: <b>税込一本</b>(入力・集計はすべて税込金額)</p>
        <p className="hint" style={{ marginTop: 8 }}>
          税抜経理へ移行する場合はスキーマ変更を伴うため、本体セッションでの対応が必要です。
        </p>
      </div>

      <div className="card">
        <h3>全社PLの閲覧範囲</h3>
        <p>現在の設定: <b>管理者のみ</b></p>
        <p className="hint" style={{ marginTop: 8 }}>
          事業部責任者にも開放する場合はフェーズ2で設定画面を追加します。
        </p>
      </div>
    </>
  );
}
